import { useMemo } from 'react';
import { gql } from '@apollo/client';

import type { Extension } from '@atlassian/forge-ui';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop';
import { useSessionData } from '@confluence/session-data';

import type { ForgeUIExtensionDataType } from '../types';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';

import { useWorkspaceARI } from './useWorkspaceARI';
import { getModuleKey } from '../utils/appData';

type GetContextToken = () => Promise<string>;

// Explicit types to satisfy TS inference for the mutation result
export type UseGetContextTokenMutationData = {
	confluence_generateForgeContextToken?: {
		success: boolean;
		errors?: Array<{
			message: string;
			code?: string;
		}>;
		forgeContextToken?: {
			jwt?: string;
			expiresAt?: string | null;
			extensionId?: string | null;
		};
	};
};

export type UseGetContextTokenMutationVariables = {
	cloudId: string;
	input: any;
};

// Cache types and helpers
export type ContextTokenData = {
	token: string;
	expiresAt?: number;
	issuedAt: number;
};

class ContextTokenCache {
	private static instance: ContextTokenCache;
	private contextTokenCacheMap: Map<string, ContextTokenData>;

	static getInstance() {
		if (!ContextTokenCache.instance) {
			ContextTokenCache.instance = new ContextTokenCache();
		}
		return ContextTokenCache.instance;
	}

	private constructor() {
		this.contextTokenCacheMap = new Map<string, ContextTokenData>();
	}

	setValue(key: string, value: ContextTokenData): void {
		this.contextTokenCacheMap.set(key, value);
	}

	getValue(key: string): ContextTokenData | undefined {
		return this.contextTokenCacheMap.get(key);
	}

	del(key: string): void {
		this.contextTokenCacheMap.delete(key);
	}
}

const TOKEN_LIFETIME_EXTRA_BUFFER_PERCENT = 0.01; // extra buffer for clock skew

// GraphQL mutation to get context token
const UseGetContextTokenMutation = gql`
	mutation useGetContextTokenMutation(
		$cloudId: ID!
		$input: ConfluenceForgeContextTokenRequestInput!
	) {
		confluence_generateForgeContextToken(cloudId: $cloudId, input: $input) {
			success
			errors {
				message
			}
			forgeContextToken {
				jwt
				expiresAt
				extensionId
			}
		}
	}
`;

/**
 * Hook to generate a context token function for Forge extensions in Confluence
 *
 * @param extension - The Forge extension configuration
 * @param extensionData - The extension context data
 * @param localId - The local identifier for this extension instance
 * @returns A function that can be called to retrieve context tokens
 */
export const useGetContextToken = (
	extension: Extension,
	extensionData: ForgeUIExtensionDataType,
	localId: string | undefined,
): GetContextToken => {
	const { cloudId } = useSessionData();
	const workspaceARI = useWorkspaceARI();
	const [runMutation] = useMutationInterop<
		UseGetContextTokenMutationData,
		UseGetContextTokenMutationVariables
	>(UseGetContextTokenMutation, {
		client: getAGGClient(),
	});

	return useMemo(() => {
		return async function getForgeContextToken(): Promise<string> {
			try {
				if (!cloudId) {
					throw new Error('CloudId is required but not available');
				}

				const cacheKey = JSON.stringify({ id: localId || extension.id, data: extensionData });
				const cached = ContextTokenCache.getInstance().getValue(cacheKey);

				if (cached) {
					// If no expiresAt provided, assume token is valid and return cached token
					if (typeof cached.expiresAt !== 'number') {
						return cached.token;
					}
					const lifetimeMs = cached.expiresAt - cached.issuedAt;
					if (
						Date.now() + Math.ceil(lifetimeMs * TOKEN_LIFETIME_EXTRA_BUFFER_PERCENT) <
						cached.expiresAt
					) {
						return cached.token;
					}
				}
				// Build the input according to the schema
				const input = {
					contextIds: [workspaceARI],
					extensionSpecificContexts: {
						appVersion: extension.appVersion,
						context: {
							appVersion: extension.appVersion,
							environmentId: extension.environmentId,
							environmentType: extension.environmentType,
							siteUrl: window?.location?.origin,
							extension: {
								location: undefined, //extensionData?.location
								content: extensionData?.content
									? {
											id: extensionData.content.id,
											type: extensionData.content.type,
											subtype: extensionData.content.subtype,
										}
									: undefined,
								space: extensionData?.space
									? {
											id: extensionData.space.id,
											key: extensionData.space.key,
										}
									: undefined,
								type: extension.type,
								selectedText: extensionData?.selectedText,
							},
							localId,
							moduleKey: getModuleKey(extension),
						},
						extensionId: extension.id,
						installationId: extension.installationId,
					},
				};

				const { data } = await runMutation({
					variables: {
						cloudId,
						input,
					},
				});

				if (!data?.confluence_generateForgeContextToken?.success) {
					const errors = data?.confluence_generateForgeContextToken?.errors || [];
					const errorMessage = errors.map((e) => e.message).join(', ') || 'Unknown error';
					throw new Error(`Token generation failed: ${errorMessage}`);
				}

				const forgeContextToken = data.confluence_generateForgeContextToken.forgeContextToken;
				if (!forgeContextToken?.jwt) {
					throw new Error('No JWT token received from GraphQL endpoint');
				}

				const tokenData: ContextTokenData = {
					token: forgeContextToken.jwt,
					expiresAt: forgeContextToken.expiresAt ? Number(forgeContextToken.expiresAt) : undefined,
					issuedAt: Date.now(),
				};
				ContextTokenCache.getInstance().setValue(cacheKey, tokenData);
				return tokenData.token;
			} catch (error: any) {
				throw new Error(`Failed to get context token: ${error.message}`);
			}
		};
	}, [extension, extensionData, localId, cloudId, runMutation, workspaceARI]);
};
